import React, { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconAttach() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
      />
    </svg>
  );
}

function IconFile() {
  return (
    <svg
      className="w-6 h-6 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

// ── Menu item row ─────────────────────────────────────────────────────────────

function MenuRow({ icon, label, chevron, onClick, disabled }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-800 rounded-lg mx-1 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
      style={{ width: "calc(100% - 8px)" }}
    >
      <span className="flex-shrink-0 w-5 h-5 text-gray-600 flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 min-w-0">{label}</span>
      {chevron && (
        <svg
          className="w-4 h-4 text-gray-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      )}
    </button>
  );
}

// ── File chip (pending attachment preview) ────────────────────────────────────

function FileChip({ file, onRemove }) {
  return (
    <div className="relative group flex-shrink-0 rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
      {file.kind === "image" ? (
        <img
          src={file.base64Url}
          alt={file.name}
          className="h-16 w-16 object-cover"
        />
      ) : (
        <div className="h-16 w-28 flex flex-col items-center justify-center gap-1 px-2">
          <IconFile />
          <span className="text-[10px] text-gray-500 truncate max-w-full">
            {file.name}
          </span>
        </div>
      )}
      <button
        type="button"
        title="Xóa"
        onClick={() => onRemove(file.id)}
        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white text-xs leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        ×
      </button>
    </div>
  );
}

// ── ChatInput ─────────────────────────────────────────────────────────────────

function ChatInput({
  input,
  setInput,
  isLoading,
  onSend,
  onStopGenerating,
  onKeyDown,
  textareaRef,
  pendingFiles = [],
  onPickFiles,
  onRemovePendingFile,
  placeholder = "Gửi tin nhắn tới ChatBot PIMATH...",
  onOpenMistakeNotebook,
  onOpenLearningPath,
  onStartQuiz,
}) {
  const fileInputRef = useRef(null);
  const attachMenuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const canSend =
    (Boolean(input.trim()) || pendingFiles.length > 0) && !isLoading;

  const openFilePicker = useCallback(() => {
    setMenuOpen(false);
    requestAnimationFrame(() => fileInputRef.current?.click());
  }, []);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files?.length && onPickFiles) void onPickFiles(files);
    e.target.value = "";
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items?.length || !onPickFiles) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await onPickFiles([file]);
        return;
      }
    }
  };

  const placeholderSoon = () => {
    setMenuOpen(false);
    toast.info("Tính năng đang được phát triển.");
  };

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [menuOpen]);

  return (
    <div className="pim-composer">
      <div className="pim-chat-container">
        {/* Hidden file input — all types */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileChange}
        />

        {/* Pending file chips */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {pendingFiles.map((f) => (
              <FileChip key={f.id} file={f} onRemove={onRemovePendingFile} />
            ))}
          </div>
        )}

        <div className="pim-composer-box">
          {/* + button with popup menu */}
          <div className="relative flex-shrink-0 self-end" ref={attachMenuRef}>
            <button
              type="button"
              title="Đính kèm và công cụ"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => !isLoading && setMenuOpen((v) => !v)}
              disabled={isLoading}
              className={`p-2 rounded-full transition-colors text-gray-400 disabled:opacity-40 hover:bg-gray-100 ${menuOpen ? "bg-gray-100 ring-2 ring-gray-200 ring-offset-1" : ""
                }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute bottom-full left-0 mb-2 w-[min(20rem,calc(100vw-2rem))] py-1.5 bg-white rounded-2xl shadow-xl border border-gray-200/80 z-[100]"
              >
                <MenuRow
                  icon={<IconAttach />}
                  label="Tải lên ảnh & tệp"
                  onClick={openFilePicker}
                />
                <MenuRow
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  }
                  label="Các tệp gần đây"
                  chevron
                  onClick={placeholderSoon}
                />

                <div className="my-1.5 mx-3 border-t border-gray-100" />

                <MenuRow
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  }
                  label="Tạo hình ảnh"
                  onClick={placeholderSoon}
                />
                <MenuRow
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                  }
                  label="Nghiên cứu chuyên sâu"
                  onClick={placeholderSoon}
                />
                <MenuRow
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  label="Tìm kiếm trên mạng"
                  onClick={placeholderSoon}
                />

                <div className="my-1.5 mx-3 border-t border-gray-100" />
                <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  PIMATH Tools
                </div>

                <MenuRow
                  icon={<span className="text-sm">📓</span>}
                  label="Sổ tay lỗi sai"
                  onClick={() => { setMenuOpen(false); onOpenMistakeNotebook?.(); }}
                />
                <MenuRow
                  icon={<span className="text-sm">❓</span>}
                  label="Bắt đầu Quiz"
                  onClick={() => { setMenuOpen(false); onStartQuiz?.(); }}
                />
                <MenuRow
                  icon={<span className="text-sm">🗺️</span>}
                  label="Lộ trình học"
                  onClick={() => { setMenuOpen(false); onOpenLearningPath?.(); }}
                />
              </div>
            )}
          </div>

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none resize-none text-sm text-gray-800 placeholder-gray-400 py-2 leading-relaxed px-1 max-h-[200px]"
            rows="1"
          />

          {isLoading ? (
            <button
              type="button"
              title="Dừng tạo câu trả lời"
              aria-label="Dừng tạo câu trả lời"
              onClick={() => onStopGenerating?.()}
              className="p-2 rounded-full bg-gray-900 hover:bg-gray-800 transition-all flex-shrink-0 ring-2 ring-gray-200 ring-offset-1"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className="p-2 rounded-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex-shrink-0"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 12h14m-7-7l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>

        <p className="pim-composer-disclaimer">
          ChatBot PIMATH có thể mắc lỗi. Hãy kiểm tra thông tin quan trọng.
        </p>
      </div>
    </div>
  );
}

export default ChatInput;
